<template lang="pug">
v-container.pa-0(style="max-width: 800px;")
  head-bar

  v-card(
    v-if="parsedData" 
    tile 
    elevation=0 
    style="border: 1px solid var(--v-basic300);"
    :style="isPrinting? 'min-height: 96vh; border-radius: 0' : 'min-height: 96vh;'"
    :class="isPrinting? 'elevation-0': ''"
  )
    v-layout.my-6(v-if="isFetching")
      v-row.fill-height.ma-0(align='center' justify='center')
        v-progress-circular.ma-auto(
          :width="2" 
          :size="50" 
          color="primary" 
          indeterminate
        )
    
    v-card-text(v-else :class="isPrintingSmall ? 'py-4 px-0' : 'pa-4'")
      v-form(ref="form")
        .client-details.text-left.mt-6
          div.s1.primary500--text INVOICE 
          v-row.pa-0
            v-col.pr-0(cols=2)
              div.p2.mb-1.basic600--text To
            v-col.pl-0(cols=10)
              div.p2.mb-1.primary500--text {{ parsedData.client.company_name }}
              div.p2.mb-1.basic600--text {{ parsedData.client.address }}
              div.p2.mb-1.basic600--text {{ parsedData.client.email }}
              div.p2.mb-1.basic600--text {{ parsedData.client.tin_number }}
            v-col.pr-0(cols=2)
              div.p2.mb-1.basic600--text From
              div.p2.mb-1.basic600--text Invoice by
              div.p2.mb-1.basic600--text Due Date
            v-col.pl-0(cols=10)
              div.p2.mb-1.primary500--text Yezza Tech Sdn Bhd
              div.p2.mb-1.primary500--text {{ parsedData.issuer }}
              div.p2.mb-1.primary500--text {{ parsedData.due_date}}

        .invoice-details.text-left
          div.s2.primary500--text Products and Services
          v-divider.my-1.basic300

          v-simple-table(dense)
            thead
              tr
                th.pa-0(style="border: none")
                  div.p2.basic600--text.text-start.mr-1 Description
                th.pa-0(style="border: none") 
                  div.p2.basic600--text.text-end.mr-1 Contract
                th.pa-0(style="border: none")
                  div.p2.basic600--text.text-end.ml-1 Value
            tbody
              template(v-for="(product, idx) in parsedData.products")
                tr(:key="idx")
                  td.text-start.pa-0(style="border: none; vertical-align:top;")
                    div.my-1
                      div.p2.primary500--text.mr-1 {{ product.name }}
                  td.text-end.pa-0(style="border: none; vertical-align:top;")
                    div.my-1
                      div.p2.primary500--text.mr-1 {{ product.contract }}
                  td.text-end.pa-0(style="border: none; vertical-align:top;")
                    div.my-1.p2.primary500--text.ml-1 RM {{ formatCurrency(product.value) }}
          v-divider.my-1.basic300

          // Discounts Section  
          div.mt-4(v-if="parsedData.discounts && parsedData.discounts.length > 0 || specialDiscount > 0")
            template(v-for="(discount, idx) in parsedData.discounts")
              div.d-flex.mt-1(:key="idx")
                div.p2.primary400--text Less ({{ discount.description }})
                v-spacer
                div.p2.primary400--text -RM {{ formatCurrency(discount.amount) }}

            // Special Discount
            div.d-flex.mt-1(v-if="specialDiscount > 0")
              div.p2.primary400--text Less (Special Discount)
              v-spacer
              div.p2.primary400--text -RM {{ formatCurrency(specialDiscount) }}

          // Total Amount
          div.d-flex.mt-4
            div.p2.primary--text Subtotal
            v-spacer
            div.p2.primary400--text RM {{ formatCurrency(subTotal) }}
          div.d-flex.mt-2  
            div.p2.primary--text 8% SST
            v-spacer
            div.p2.primary400--text RM {{ formatCurrency(taxAmount) }}
          div.d-flex.mt-4
            div.h6.primary--text Total Amount (Inc SST)
            v-spacer
            div.h6.primary--text RM {{ formatCurrency(totalAmount) }}

        // Payment Actions
        div.pt-6
          v-btn.button-AA-medium.white--text.mt-2(
            color="textColorAccent"
            outlined
            block
            depressed 
            height=32
            @click="generateInvoicePDF"
          ) Download Invoice
</template>

<script>
import HeadBar from '@/components/forms/HeadBar.vue'
import axios from 'axios'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'

export default {
  name: 'CustomerInvoice',
  components: {
    HeadBar
  },
  props: ['lead'],
  data: () => ({
    isFetching: true,
    invoice: null,
    parsedData: null,
    error: null
  }),
  computed: {
    isPrinting() {
      return this.$route.path.includes('print-invoices')
    },
    isPrintingSmall() {
      return this.$route.path.includes('print-invoices-small')
    },
    subTotal() {
      if (!this.parsedData) return 0

      // Calculate total product value
      const productsTotal = this.parsedData.products.reduce((sum, product) => {
        return sum + Number(product.value)
      }, 0)

      // Calculate total discounts (including special discount)
      const discountsTotal = this.parsedData.discounts?.reduce((sum, discount) => {
        return sum + Number(discount.amount || 0)
      }, 0) || 0

      return productsTotal - discountsTotal - this.specialDiscount
    },
    totalAmount() {
      if (!this.parsedData) return 0
      
      return this.parsedData.total.amount*1.08
    },
    taxAmount() {
      if (!this.parsedData?.total?.amount) return 0
      return parseFloat((this.parsedData.total.amount / 100 * 8).toFixed(2))
    },
    specialDiscount() {
      if (!this.parsedData) return 0
      return Number(this.parsedData.special_discount || 0)
    }
  },
  async mounted() {
    await this.fetchInvoiceData()
  },
  methods: {
    async fetchInvoiceData() {
      try {
        const response = await axios.get(`/leads/${this.lead.pid}/`)
        this.invoice = response.data
        console.log(this.invoice)
        const invoiceField = response.data.extra_fields_data.find(
          field => field.name === 'Invoice' && field.enabled
        )
        
        if (invoiceField?.value) {
          this.parsedData = JSON.parse(invoiceField.value)
          console.log('Parsed invoice data:', this.parsedData) // Debug log
        } else {
          throw new Error('Invoice data not found')
        }
      } catch (error) {
        console.error('Error fetching invoice data:', error)
        this.error = error.message
      } finally {
        this.isFetching = false
      }
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString()
    },
    generateInvoicePDF() {
      const doc = new jsPDF()

      // Company info
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text('YEZZA TECH SDN BHD', 10, 20)
      doc.setFont('helvetica', 'normal')
      doc.text('K-3-01, IOI Conezion, IOI Resort City,', 10, 25)
      doc.text('Persiaran IRC 3, 62502 PUTRAJAYA', 10, 30)
      doc.text('hello@yezza.com', 10, 35)
      doc.text('Business Reg: 202001010294 (1366614A)', 10, 40)

      // Invoice title
      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(255, 0, 38)
      doc.text('INVOICE', 10, 60)

      // Bill to section
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(0)
      doc.setFontSize(10)
      doc.text('Bill To :', 10, 70)
      doc.setFont('helvetica', 'normal')
      doc.text(this.parsedData.client.company_name, 10, 75)
      doc.text(this.parsedData.client.email, 10, 80)
      doc.text(this.parsedData.client.address, 10, 85, { maxWidth: 120 })

      // Invoice details
      doc.text(`Invoice By: ${this.parsedData.issuer}`, 150, 70)
      doc.text(`Invoice Number: ${this.invoice.rid}`, 150, 75)
      doc.text(`Due Date: ${this.parsedData.due_date}`, 150, 80)

      // Products table
      doc.autoTable({
        startY: 100,
        head: [['Product', 'Contract', 'Value']],
        body: this.parsedData.products.map(p => [
          p.name,
          p.contract,
          `RM ${this.formatCurrency(p.value)}`
        ]),
        headStyles: {
          fillColor: [255, 217, 219],
          textColor: [255, 0, 38],
          fontStyle: 'normal',
          halign: 'left',
          valign: 'middle'
        },
        bodyStyles: {
          fillColor: [255, 255, 255],
          textColor: [0],
          fontStyle: 'normal',
          halign: 'left'
        }
      })

      // Totals section
      const pageWidth = doc.internal.pageSize.width
      const rightMargin = 15
      const alignRightX = pageWidth - rightMargin
      let yPos = doc.lastAutoTable.finalY + 10

      const addRightAlignedText = (text, y) => {
        doc.text(text, alignRightX, y, { align: 'right' })
      }

      // Discounts
      if (this.parsedData.discounts) {
        this.parsedData.discounts.forEach(d => {
          addRightAlignedText(
            `Less (${d.description}): RM ${this.formatCurrency(d.amount)}`, 
            yPos
          )
          yPos += 5
        })
      }

      // Special Discount
      if (this.specialDiscount > 0) {
        addRightAlignedText(
          `Less (Special Discount): RM ${this.formatCurrency(this.specialDiscount)}`, 
          yPos
        )
        yPos += 5
      }

      // Final totals
      yPos += 5
      addRightAlignedText(`SubTotal: RM ${this.formatCurrency(this.subTotal)}`, yPos)
      yPos += 5
      addRightAlignedText(`8% SST: RM ${this.formatCurrency(this.taxAmount)}`, yPos)
      yPos += 10
      doc.setFont('helvetica', 'bold')
      addRightAlignedText(
        `Total Amount (Inc SST): RM ${this.formatCurrency(this.parsedData.total.amount+this.taxAmount)}`, 
        yPos
      )

      // Footer
      const footerY = doc.internal.pageSize.height - 60
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('YEZZA TECH SDN BHD', 10, footerY)

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.text('Maybank Islamic : 5621 4242 4921', 10, footerY + 5)
      doc.text('Swift Code : MBBEMYKL', 10, footerY + 10)
      doc.text(`Reference: INV${this.invoice.rid}`, 10, footerY + 15)

      doc.setDrawColor(200)
      doc.line(10, footerY + 20, 200, footerY + 20)

      // Address
      doc.setFont('helvetica', 'bold')
      doc.text('Office Address', 10, footerY + 30)
      doc.setFont('helvetica', 'normal')
      doc.text('K-3-01, Conezion, Persiaran IRC3,', 10, footerY + 35)
      doc.text('IOI Resort City, 62502 Putrajaya', 10, footerY + 40)

      doc.save(`Invoice_${this.invoice.rid}_${this.parsedData.client.company_name}.pdf`)
    }
  }
}
</script>

<style scoped>
.dotted {
  border-top: 2px dashed var(--v-primary400);
}
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>